import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import { CreditLineByProduct } from './types';

export const getCreditLineApprovals = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${id}/approval`,
    dispatch,
  });
  if (response) return response.data;
};

export const exportFactoringCreditLine = async (
  dispatch: Dispatch,
  id: number,
  query: string,
): Promise<Blob | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/report/pdf/factoring/creditLine/${id}${query ? `?${query}` : ''}`,
    dispatch,
    responseType: 'blob',
  });
  if (response) return response.data;
};

export const getCreditLineByProduct = (
  dispatch: Dispatch,
  businessIdentifier?: string,
  product?: string,
) =>
  useQuery({
    queryKey: ['creditLine', 'product', businessIdentifier, product],
    queryFn: async (): Promise<CreditLineByProduct | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/creditLine/express/${businessIdentifier}/product/${product}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!businessIdentifier && !!product,
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

export const createCreditLineExpress = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create', 'creditLineExpress'],
    mutationFn: async (data: {
      businessIdentifier: string;
      productType: string;
    }) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/creditLine/express`,
        data,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          'creditLine',
          'product',
          variables.businessIdentifier,
          variables.productType,
        ],
      });
    },
  });
};

export const postCreditLineExpress = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/express`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableCreditLineProducts = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/availableProducts/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};
